import { StringHelpers } from "../helpers/string-helpers";
import { TimeframeHelper } from "../helpers/timeframe-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { DrawOrderExecutionModel } from "../models/draw-trade-order/draw-order-execution.model";
import { IComponent } from "./component.interface";
import { PnlComponent } from "./pnl.component";

export class DrawOrderExecutionComponent implements IComponent {
    public id = '';

    private base: IChartContainer;
    private model: Array<DrawOrderExecutionModel> = [];
    private isRemovingForSwitchingSymbol = false;
    private annotations = {};

    private canDraw = false;

    constructor(base: IChartContainer) {
        this.id = StringHelpers.newGuid();
        this.base = base;

        if (!this.base.marketingWebChartMode) {
            this.canDraw = true;
        }

        this.base.tiChartSeries?.subscribeExtremeChange(this.id, () => {
            this.draw();
        });
        this.base.tiChartSeries?.subscribeRedrawChange(this.id, () => {
            this.draw();
        });
        this.base.tiChartSeries?.subscribeChartLoad(this.id, () => {
            setTimeout(() => {
                this.draw();
            }, 500);
        });
        this.subscribeDrawOrderExecutionData();
    }

    public enable() {
        if (this.canDraw) return;

        this.canDraw = true;
        this.draw();
    }

    public disable() {
        this.canDraw = false;
        if (this.annotations) {
            for (const m in this.annotations) {
                if (this.annotations[m]) {
                    this.annotations[m].destroy();
                    this.annotations[m] = null;
                }
            }
        }

        this.annotations = {};
    }

    public createElement() {
        const highchartContainer = this.base.tiChartSeries.element.findFirst('.highcharts-container');
        if (highchartContainer) {
            this.isRemovingForSwitchingSymbol = false;
        }
    }

    public remove() {
        this.isRemovingForSwitchingSymbol = true;
        if (this.base.marketingWebChartMode) {
            this.canDraw = false;
        }

        this.removeAnnotations();
    }

    private removeAnnotations() {
        if (this.annotations) {
            for (const m in this.annotations) {
                if (this.annotations[m]) {
                    this.annotations[m].destroy();
                    this.annotations[m] = null;
                }
            }
        }
        
        if (this.base.marketingWebChartMode) {
            this.model = [];
        } else {
            if (this.base.tiChartSeries?.symbol) {
                this.model = this.model.filter(m => m.symbol == this.base.tiChartSeries?.symbol);
            }
        }

        this.annotations = {};
        
    }

    private subscribeDrawOrderExecutionData() {
        this.base.chartService.subscribeDrawOrderExecutionData((model: Array<DrawOrderExecutionModel>) => {
            this.model = model;
            this.draw();
        });

        // setTimeout(() => {
        //     this.model = [
        //         new DrawOrderExecutionModel({
        //             tooltip: 'test',
        //             symbol: 'CHNR',
        //             // imageAnnotationName: 'EXECUTION_BUY_TO_COVER_DARK',
        //             imageAnnotationName: 'EXECUTION_BUY_TO_COVER_DARK',
        //             x: 345,
        //             y: 1.1
        //         })
        //     ]

        //     this.draw();
        // }, 5000);
    }

    private draw() {
        if (!this.canDraw) {
            return;
        }

        if (this.isRemovingForSwitchingSymbol || !this.base.tiChartSeries) {
            this.removeAnnotations();
            return;
        }
        
        const pnlComponent = this.base.getComponentsByType(PnlComponent)[0] as PnlComponent;
        const selectedAccount = pnlComponent?.selectedAccount;

        let buySellFlags: Array<DrawOrderExecutionModel> = [];
        if (selectedAccount) {
            let filteredFlags: Array<DrawOrderExecutionModel> = [];
            for (const f of this.model) {
                if (f.accounts.some(x => x.name == selectedAccount)) {
                    filteredFlags.push(f);
                }
            }

            buySellFlags = filteredFlags;
        }

        if (!this.base.tiChartSeries?.chartStyleSettings?.chartSettingsPopupModel?.displayChartSettingBuySellFlags || !buySellFlags?.length) {
            this.removeAnnotations();
            return;
        }
        

        buySellFlags = buySellFlags.sort((a, b) => {
            const aMax = Math.max(...(a.executionIds?.map(id => parseFloat(id) || 0) || [0]));
            const bMax = Math.max(...(b.executionIds?.map(id => parseFloat(id) || 0) || [0]));
            return aMax - bMax;
        });

        const chartExtraSmall = this.base.isExtraSmall();
        let startZIndex = 0;
        for (const item of buySellFlags) {
            const isIntraday = TimeframeHelper.timeframeIntraday(this.base.tiChartSeries.currentTimeframe);
            const xValue = isIntraday ? Math.floor(item.x) : Math.ceil(item.x);
            const imageAndClass = item.getImageAndClass(this.base.basePath, 2);
            const image = imageAndClass[0];
            const className = imageAndClass[1];

            const xAxis = this.base.tiChartSeries.highStockChart?.xAxis?.[0];
            const yAxis = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
            const mainChartSerie: any = this.base.tiChartSeries.highStockChart?.series?.[0];
            const key = item.getKey();
            if (!mainChartSerie ||
                !xAxis ||
                !yAxis ||
                item.symbol != this.base.tiChartSeries.symbol ||
                !mainChartSerie.processedXData.some((x: number) => x == xValue) || 
                item.y < yAxis.min ||
                item.y > yAxis.max) {
                if (this.annotations[key]) {
                    this.annotations[key].destroy();
                    this.annotations[key] = null;
                }

                continue;
            }

            let left = xAxis?.toPixels(xValue);
            if (className == 'right') {
                left -= 25;
            }

            let top = yAxis?.toPixels(item.y, true);
            if (!chartExtraSmall) {
                if (this.base.element.containClass('one')) {
                    top += 36;
                } else {
                    top += 58;
                }

                top -= 10;
                if (!this.base.marketingWebChartMode) {
                    top -= 12;
                }
            } else {
                top += 8;
            }

            const newZIndex = 17 + startZIndex;
            if (this.annotations[key] && !!this.annotations[key].image?.element && this.base.chartWrapper.contains(this.annotations[key].image.element)) {
                this.annotations[key].image?.attr({ x: left, y: top });
                this.annotations[key].g?.attr({ zIndex: newZIndex });
            } else {
                const renderer = this.base.tiChartSeries.highStockChart.renderer;
                const g = this.base.tiChartSeries.highStockChart.renderer.g(key).attr({
                    zIndex: newZIndex
                });
                const width = 23;
                const height = 20;
                const imageElement = renderer.image(image, left, top, width, height);
                imageElement.add(g);

                const titleElement = renderer.createElement('title');
                titleElement.add(g);
                this.annotations[key] = {
                    g: g,
                    image: imageElement,
                    left: left,
                    top: top,
                    width: width,
                    height: height,
                    title: titleElement,
                    destroy: () => {
                        g?.destroy();
                    }
                };
                g.add();
                setTimeout(() => {
                    const title = this.base.chartWrapper.querySelector('.highcharts-' + key + ' title');
                    if (title)
                        title.innerHTML = item.tooltip;
                }, 500);
            }

            startZIndex++;
        }

        //remove annotations
        for (const k of Object.keys(this.annotations)) {
            if (!buySellFlags.some((x: DrawOrderExecutionModel) => x.getKey() == k)) {
                this.annotations[k]?.destroy();
                this.annotations[k] = null;
            }
        }
    }
}